'use client';

import { useCallback, useMemo, useState } from 'react';
import type { PrimaryNavItem } from '../types';
import { DEFAULT_GROUP_ID } from '../config';

interface UseNavigationProps {
  navigation: PrimaryNavItem[];
  defaultGroupId?: number;
  /** 传入时由 URL query 控制导航；undefined 保持原有内部状态模式。 */
  selectedGroupId?: number | null;
  onNavigationChange?: (groupId: number) => void;
}

interface UseNavigationReturn {
  primaryIndex: number;
  secondaryIndex: number;
  currentGroupId: number;
  /** 选中一级导航 */
  selectPrimary: (index: number) => void;
  /** 选中二级导航 */
  selectSecondary: (index: number) => void;
  /** PC 端：点击已选中的二级导航取消选中 */
  toggleSecondary: (primaryIndex: number, secondaryIndex: number) => void;
  /** 当前一级导航数据 */
  currentPrimary: PrimaryNavItem;
  /** 当前一级导航下的二级导航列表 */
  currentSecondaryNav: PrimaryNavItem['secondaryNav'];
  /** PC 端选中的二级导航索引（按一级索引） */
  pcSelectedSecondary: Record<number, number | null>;
}

export function getDefaultPcSecondaryLocation(navigation: PrimaryNavItem[]) {
  for (let pIndex = 0; pIndex < navigation.length; pIndex += 1) {
    const sIndex = navigation[pIndex]?.secondaryNav.findIndex((item) => item.isDefault);
    if (sIndex != null && sIndex >= 0) {
      return { primaryIndex: pIndex, secondaryIndex: sIndex };
    }
  }

  for (let pIndex = 0; pIndex < navigation.length; pIndex += 1) {
    const sIndex = navigation[pIndex]?.secondaryNav.findIndex((item) => !item.hidden);
    if (sIndex != null && sIndex >= 0) {
      return { primaryIndex: pIndex, secondaryIndex: sIndex };
    }
  }

  return null;
}

export function getNavigationLocationByGroupId(navigation: PrimaryNavItem[], groupId?: number) {
  if (groupId === undefined) return null;

  for (let primaryIndex = 0; primaryIndex < navigation.length; primaryIndex += 1) {
    const secondaryIndex = navigation[primaryIndex]?.secondaryNav.findIndex(
      (item) => item.groupId === groupId,
    );
    if (secondaryIndex != null && secondaryIndex >= 0) {
      return { primaryIndex, secondaryIndex };
    }
  }

  return null;
}

export function getNextPcNavigationLocation(
  navigation: PrimaryNavItem[],
  currentSelected: number | null | undefined,
  primaryIndex: number,
  secondaryIndex: number,
) {
  if (currentSelected === secondaryIndex) {
    return getDefaultPcSecondaryLocation(navigation);
  }

  const target = navigation[primaryIndex]?.secondaryNav[secondaryIndex];
  if (!target) return null;

  return { primaryIndex, secondaryIndex };
}

function createPcSelectedSecondary(
  navigation: PrimaryNavItem[],
  selected?: { primaryIndex: number; secondaryIndex: number } | null,
): Record<number, number | null> {
  const next = navigation.reduce<Record<number, number | null>>((acc, _, index) => {
    acc[index] = null;
    return acc;
  }, {});

  if (selected) {
    next[selected.primaryIndex] = selected.secondaryIndex;
  }

  return next;
}

export function useNavigation({
  navigation,
  defaultGroupId,
  selectedGroupId,
  onNavigationChange,
}: UseNavigationProps): UseNavigationReturn {
  const initialLocation = getDefaultPcSecondaryLocation(navigation);
  const [primaryIndex, setPrimaryIndex] = useState(initialLocation?.primaryIndex ?? 0);

  // PC 端每个一级导航维护独立的二级选中状态
  const [pcSelectedSecondary, setPcSelectedSecondary] = useState<Record<number, number | null>>(
    () => createPcSelectedSecondary(navigation, initialLocation),
  );

  // M 端的二级选中状态
  const [mobileSecondaryIndex, setMobileSecondaryIndex] = useState<number | null>(null);

  const controlledLocation = useMemo(() => {
    if (selectedGroupId === undefined) return null;
    return (
      getNavigationLocationByGroupId(navigation, selectedGroupId ?? undefined) ??
      getDefaultPcSecondaryLocation(navigation)
    );
  }, [navigation, selectedGroupId]);
  const resolvedPrimaryIndex = controlledLocation?.primaryIndex ?? primaryIndex;
  const resolvedPcSelectedSecondary = useMemo(
    () =>
      controlledLocation
        ? createPcSelectedSecondary(navigation, controlledLocation)
        : pcSelectedSecondary,
    [controlledLocation, navigation, pcSelectedSecondary],
  );
  const resolvedMobileSecondaryIndex =
    selectedGroupId !== undefined
      ? (controlledLocation?.secondaryIndex ?? null)
      : mobileSecondaryIndex;

  const currentPrimary = navigation[resolvedPrimaryIndex] || navigation[0];
  const currentSecondaryNav = currentPrimary?.secondaryNav || [];

  // 获取当前 group_id
  const getCurrentGroupId = useCallback(
    (pIndex: number, sIndex: number | null): number => {
      const primary = navigation[pIndex];
      if (!primary) return defaultGroupId || DEFAULT_GROUP_ID;

      if (sIndex === null) {
        // 选中默认（ALL）
        const defaultNav = primary.secondaryNav.find((s) => s.isDefault);
        return defaultNav?.groupId || defaultGroupId || DEFAULT_GROUP_ID;
      }

      const secondary = primary.secondaryNav[sIndex];
      return secondary?.groupId || defaultGroupId || DEFAULT_GROUP_ID;
    },
    [navigation, defaultGroupId],
  );

  const currentGroupId = getCurrentGroupId(
    resolvedPrimaryIndex,
    resolvedMobileSecondaryIndex ?? resolvedPcSelectedSecondary[resolvedPrimaryIndex] ?? null,
  );

  const selectPrimary = useCallback(
    (index: number) => {
      if (index < 0 || index >= navigation.length) return;
      setPrimaryIndex(index);
      // M 端重置为第一个二级导航
      setMobileSecondaryIndex(0);
      const groupId = getCurrentGroupId(index, 0);
      onNavigationChange?.(groupId);
    },
    [navigation, getCurrentGroupId, onNavigationChange],
  );

  const selectSecondary = useCallback(
    (index: number) => {
      setMobileSecondaryIndex(index);
      const groupId = getCurrentGroupId(resolvedPrimaryIndex, index);
      onNavigationChange?.(groupId);
    },
    [resolvedPrimaryIndex, getCurrentGroupId, onNavigationChange],
  );

  const toggleSecondary = useCallback(
    (pIndex: number, sIndex: number) => {
      const nextLocation = getNextPcNavigationLocation(
        navigation,
        resolvedPcSelectedSecondary[pIndex],
        pIndex,
        sIndex,
      );
      if (!nextLocation) return;

      // 同步切换一级导航，确保 currentGroupId 等逻辑基于正确的一级导航
      setPrimaryIndex(nextLocation.primaryIndex);
      setPcSelectedSecondary(createPcSelectedSecondary(navigation, nextLocation));
      const groupId = getCurrentGroupId(nextLocation.primaryIndex, nextLocation.secondaryIndex);
      onNavigationChange?.(groupId);
    },
    [navigation, resolvedPcSelectedSecondary, getCurrentGroupId, onNavigationChange],
  );

  // 当前一级导航下的 PC 选中状态
  const currentPcSecondaryIndex = resolvedPcSelectedSecondary[resolvedPrimaryIndex];

  return {
    primaryIndex: resolvedPrimaryIndex,
    secondaryIndex: resolvedMobileSecondaryIndex ?? currentPcSecondaryIndex ?? -1,
    currentGroupId,
    selectPrimary,
    selectSecondary,
    toggleSecondary,
    currentPrimary,
    currentSecondaryNav,
    pcSelectedSecondary: resolvedPcSelectedSecondary,
  };
}
